import axios from 'axios';
import { performSimpleLogout } from '../utils/logout';

console.log('🚀 API SERVICE LOADED - CODE IS UPDATED!');

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

console.log('📡 Axios instance created with baseURL:', api.defaults.baseURL);

// Function to clear auth header
export const clearAuthHeader = () => {
  delete api.defaults.headers.common['Authorization'];
};

// Function to set auth header
export const setAuthHeader = (token) => {
  console.log('🔧 setAuthHeader called with token:', token ? `${token.substring(0, 20)}...` : 'null');
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('✅ Authorization header set in axios defaults');
    console.log('📋 Current axios defaults:', api.defaults.headers.common);
  } else {
    clearAuthHeader();
  }
};

// Function to handle authentication errors and logout
const handleAuthError = (error) => {
  // Check if this is a login/register/public request - don't logout for these
  const publicEndpoints = [
    '/auth/login',
    '/auth/register',
    '/auth/verify-email',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/setup-password',
    '/cms',
    '/flights/search',
    '/airports',
    '/countries',
    '/2fa/verify-security-questions'
  ];

  const isPublicEndpoint = publicEndpoints.some(endpoint =>
    error.config?.url?.includes(endpoint)
  );

  if (!isPublicEndpoint) {
    // Use the centralized simple logout function
    performSimpleLogout();
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    console.log('🌐 API Request:', config.method?.toUpperCase(), config.url);
    console.log('🔑 Token from localStorage:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');
    console.log('🔑 Authorization header before:', config.headers.Authorization);

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('✅ Authorization header set in request:', config.headers.Authorization ? 'YES' : 'NO');
    } else {
      console.warn('⚠️ No token found in localStorage!');
    }

    // Don't set Content-Type for FormData - let axios/browser handle it
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      console.log('📤 FormData detected - Content-Type will be set by browser');
    } else if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }

    console.log('📤 Final request headers:', config.headers);
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.config.url, 'Status:', response.status);
    console.log('📦 Response data:', response.data);
    return response.data;
  },
  (error) => {
    console.error('❌ API Error Response:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.response?.data?.message,
      data: error.response?.data
    });

    // Handle authentication errors (401 Unauthorized)
    if (error.response?.status === 401) {
      console.warn('🚨 401 Unauthorized - Triggering logout');
      handleAuthError(error);
    }

    // Handle forbidden errors (403 Forbidden) - also logout
    if (error.response?.status === 403) {
      const message = error.response?.data?.message || '';
      console.warn('🚨 403 Forbidden:', message);
      // If it's a token-related forbidden error, logout
      if (message.toLowerCase().includes('token') ||
        message.toLowerCase().includes('unauthorized') ||
        message.toLowerCase().includes('forbidden')) {
        console.warn('🚨 Token-related 403 - Triggering logout');
        handleAuthError(error);
      }
    }

    // Extract the error message from the response
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';

    // Create a proper error object that matches what the auth slice expects
    const customError = new Error(errorMessage);
    customError.status = error.response?.status;
    customError.data = error.response?.data;
    customError.isInactive = error.response?.data?.isInactive || false;

    return Promise.reject(customError);
  }
);

export default api;